import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, MapPin, CreditCard, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import { usePartner } from '../lib/partnerContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RadarPaiementDirectPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client');
  const parcelleId = searchParams.get('parcelle');
  const partner = usePartner(searchParams.toString());
  const partnerSubdomain = partner.subdomain;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parcelle, setParcelle] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchParcelle = async () => {
      if (!clientId || !parcelleId) {
        setError('Lien invalide. Veuillez réessayer depuis votre email.');
        setLoading(false);
        return;
      }

      try {
        // Fetch parcelle info
        const response = await axios.get(`${API}/api/radar/parcelle/${parcelleId}`);
        setParcelle(response.data);
      } catch (err) {
        setError('Parcelle non trouvée. Le lien a peut-être expiré.');
      } finally {
        setLoading(false);
      }
    };

    fetchParcelle();
  }, [clientId, parcelleId]);

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/api/radar/create-checkout-session?client_id=${clientId}&parcelle_id=${parcelleId}${partnerSubdomain ? '&partner_subdomain=' + partnerSubdomain : ''}`
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du paiement. Veuillez réessayer.');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-400 mx-auto mb-4" />
          <p className="text-white/70">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-white/10 border-white/20 max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Erreur</h2>
            <p className="text-white/70 mb-6">{error}</p>
            <Button
              onClick={() => navigate(partner.link('/radar'))}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Nouvelle recherche
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="bg-white/10 border-white/20 max-w-lg w-full">
        <CardContent className="pt-8 pb-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-8 h-8 text-purple-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Obtenir l'identité du propriétaire</h1>
          </div>

          {/* Parcelle Info */}
          {parcelle && (
            <div className="bg-white/5 p-4 rounded-lg mb-6">
              <h3 className="text-white/60 text-sm mb-2">Parcelle sélectionnée :</h3>
              <p className="text-white font-semibold text-lg">
                {parcelle.section}-{parcelle.numero}
              </p>
              <p className="text-white/80">{parcelle.commune} ({parcelle.code_postal})</p>
              {parcelle.surface && (
                <p className="text-white/60 text-sm mt-1">{parcelle.surface} m²</p>
              )}
            </div>
          )}

          {/* What you get */}
          <div className="bg-purple-500/20 p-4 rounded-lg border border-purple-500/30 mb-6">
            <h4 className="text-white font-medium mb-3">Ce que vous allez recevoir :</h4>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-white/90">
                <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
                <span>Nom et prénom du propriétaire actuel</span>
              </li>
              <li className="flex items-center gap-2 text-white/90">
                <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
                <span>Adresse du propriétaire</span>
              </li>
            </ul>
          </div>

          {/* Delay info */}
          <div className="bg-blue-500/20 p-4 rounded-lg border border-blue-500/30 mb-6">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-5 h-5 text-blue-400" />
              <span className="text-white font-medium">Délai : 7 à 10 jours ouvrés</span>
            </div>
            <p className="text-white/70 text-sm">
              Notre expert effectue les recherches officielles auprès des services cadastraux.
            </p>
          </div>

          {/* Price & Pay Button */}
          <div className="text-center">
            <div className="mb-4">
              <span className="text-4xl font-bold text-white">49 €</span>
              <span className="text-white/60 ml-2">TTC</span>
            </div>
            
            <Button
              onClick={handlePayment}
              disabled={processing}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 py-6 text-lg"
            >
              {processing ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Redirection vers le paiement...</>
              ) : (
                <><CreditCard className="w-5 h-5 mr-2" /> Payer et obtenir le nom</>
              )}
            </Button>
            
            <p className="text-white/50 text-xs mt-4">
              🔒 Paiement 100% sécurisé par Stripe
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
